const BASE = '/api/admin/coverage';
const OPERATORS_BASE = '/api/admin/operators';

async function authHeaders({ json = false } = {}) {
  const { supabase } = await import('./supabaseClient');
  const { data: { session } } = await supabase.auth.getSession();
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

function apiError(data, status, fallback) {
  const error = new Error(data?.error || fallback || `درخواست با خطای ${status} انجام نشد.`);
  error.code = data?.code || 'COVERAGE_INTAKE_FAILED';
  error.userMessage = data?.error || fallback || 'درخواست محدوده انجام نشد. دوباره تلاش کنید.';
  error.details = data?.details;
  return error;
}

async function getReferenceUrl(url, signal) {
  let response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: await authHeaders(),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    const network = new TypeError('Network request failed');
    network.userMessage = 'ارتباط با سرور برقرار نشد. اتصال شبکه را بررسی کنید.';
    throw network;
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw apiError(data, response.status);
  return data;
}

async function mutateJson(base, method, path, body, audit) {
  const payload = {
    ...(body || {}),
    ...(audit?.requestId ? { request_id: audit.requestId } : {}),
    ...(audit?.reason ? { audit_reason: audit.reason } : {}),
  };
  let response;
  try {
    response = await fetch(`${base}${path}`, {
      method,
      headers: await authHeaders({ json: true }),
      body: JSON.stringify(payload),
    });
  } catch {
    return {
      error: 'ارتباط با سرور برقرار نشد. اتصال شبکه را بررسی و دوباره تلاش کنید.',
      code: 'NETWORK_ERROR',
    };
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      error: data?.error || `درخواست با خطای ${response.status} انجام نشد.`,
      code: data?.code || 'COVERAGE_REGISTRY_FAILED',
      details: data?.details,
      httpStatus: response.status,
    };
  }
  return data || {};
}

export const listIntakeCarriers = (signal) => getReferenceUrl(`${BASE}/carriers`, signal);

export const listIntakeServices = (carrierId, signal) => getReferenceUrl(
  `${BASE}/services?carrier_id=${encodeURIComponent(carrierId)}`,
  signal,
);

export const listCoverageRegistryServices = (carrierId, signal) => getReferenceUrl(
  `${OPERATORS_BASE}/${encodeURIComponent(carrierId)}/services`,
  signal,
);

export const createCoverageRegistryService = (carrierId, body, audit) => mutateJson(
  OPERATORS_BASE,
  'POST',
  `/${encodeURIComponent(carrierId)}/services`,
  body,
  audit,
);

export const retireCoverageRegistryService = (carrierId, serviceId, audit) => mutateJson(
  OPERATORS_BASE,
  'POST',
  `/${encodeURIComponent(carrierId)}/services/${encodeURIComponent(serviceId)}/retire`,
  {},
  audit,
);

export const updateCoverageRegion = (regionId, body, audit) => mutateJson(
  BASE,
  'PATCH',
  `/regions/${encodeURIComponent(regionId)}`,
  body,
  audit,
);

export const retireCoverageRegion = (regionId, audit) => mutateJson(
  BASE,
  'POST',
  `/regions/${encodeURIComponent(regionId)}/retire`,
  {},
  audit,
);

export const retireCoverageImport = (importId, audit) => mutateJson(
  BASE,
  'POST',
  `/imports/${encodeURIComponent(importId)}/retire`,
  {},
  audit,
);

export async function parseCoverageGeometry({
  carrierId,
  serviceType,
  regionName = '',
  file,
  onProgress,
  onValidation,
}) {
  const headers = await authHeaders();
  const form = new FormData();
  form.append('carrier_id', carrierId);
  form.append('service_type', serviceType);
  form.append('region_name', regionName);
  form.append('file', file);

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${BASE}/intake/parse`);
    Object.entries(headers).forEach(([name, value]) => request.setRequestHeader(name, value));
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    request.upload.onload = () => onValidation?.();
    request.onerror = () => reject(new TypeError('Network request failed'));
    request.onload = () => {
      let data = {};
      try { data = request.responseText ? JSON.parse(request.responseText) : {}; } catch { data = {}; }
      if (request.status < 200 || request.status >= 300) {
        reject(apiError(data, request.status, 'بارگذاری یا اعتبارسنجی فایل ناموفق بود.'));
        return;
      }
      resolve(data);
    };
    request.send(form);
  });
}

export async function commitCoverageGeometry({ commit, requestId, reason }) {
  let response;
  try {
    response = await fetch(`${BASE}/intake/commit`, {
      method: 'POST',
      headers: await authHeaders({ json: true }),
      body: JSON.stringify({ commit, request_id: requestId, reason }),
    });
  } catch {
    throw new TypeError('Network request failed');
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw apiError(data, response.status, 'ثبت محدوده در مرحله‌بندی ناموفق بود.');
  return data;
}
